// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Internal notes on an estimate.
//
// Distinct from itemState[].notes, which PRINTS — that one is a client-facing
// annotation on the line. These never leave the building: they carry an
// author, a time and a resolution, and they hang off whatever they are about.
//
// Unresolved doubles as a to-do list, which is why there is no separate task
// object. One primitive serves the estimator working alone and the two people
// working the same file.

import { supabase } from "./supabase";

export type NoteAnchorKind = "project" | "division" | "subcategory" | "item";

export interface ProjectNote {
  id: string;
  projectId: string;
  parentId: string | null;
  body: string;
  anchorKind: NoteAnchorKind;
  anchorId: string | null;
  anchorLabel: string | null;
  authorId: string;
  authorName: string;
  mentions: string[];
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
}

const rowToNote = (r: any): ProjectNote => ({
  id: r.id,
  projectId: r.project_id,
  parentId: r.parent_id ?? null,
  body: r.body ?? "",
  anchorKind: (r.anchor_kind ?? "project") as NoteAnchorKind,
  anchorId: r.anchor_id ?? null,
  anchorLabel: r.anchor_label ?? null,
  authorId: r.author_id,
  authorName: r.author_name ?? "",
  mentions: r.mentions ?? [],
  resolvedAt: r.resolved_at ?? null,
  resolvedBy: r.resolved_by ?? null,
  createdAt: r.created_at,
});

const COLS =
  "id,project_id,parent_id,body,anchor_kind,anchor_id,anchor_label," +
  "author_id,author_name,mentions,resolved_at,resolved_by,created_at";

export async function loadNotes(projectId: string): Promise<ProjectNote[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from("project_notes").select(COLS)
    .eq("project_id", projectId)
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(rowToNote);
}

export interface NewNote {
  projectId: string;
  body: string;
  anchorKind?: NoteAnchorKind;
  anchorId?: string | null;
  anchorLabel?: string | null;
  parentId?: string | null;
  mentions?: string[];
}

export async function addNote(
  note: NewNote, authorId: string, authorName: string,
): Promise<ProjectNote> {
  if (!supabase) throw new Error("Not connected");
  const { data, error } = await supabase.from("project_notes").insert({
    project_id: note.projectId,
    parent_id: note.parentId ?? null,
    body: note.body.trim(),
    anchor_kind: note.anchorKind ?? "project",
    anchor_id: note.anchorId ?? null,
    anchor_label: note.anchorLabel ?? null,
    author_id: authorId,
    author_name: authorName,
    mentions: note.mentions ?? [],
  }).select(COLS).single();
  if (error) throw new Error(error.message);
  return rowToNote(data);
}

/** Resolve or reopen. Anyone on the project may do this; that is the point. */
export async function setNoteResolved(
  noteId: string, resolved: boolean, userId: string,
): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from("project_notes").update({
    resolved_at: resolved ? new Date().toISOString() : null,
    resolved_by: resolved ? userId : null,
    updated_at: new Date().toISOString(),
  }).eq("id", noteId);
  if (error) throw new Error(error.message);
}

/** Only the author's own; the delete policy enforces it server-side too. */
export async function deleteNote(noteId: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from("project_notes").delete().eq("id", noteId);
  if (error) throw new Error(error.message);
}

// ── Unread ───────────────────────────────────────────────────────────────

export async function markNotesSeen(projectId: string, userId: string): Promise<void> {
  if (!supabase) return;
  await supabase.from("project_note_views").upsert(
    { user_id: userId, project_id: projectId, seen_at: new Date().toISOString() },
    { onConflict: "user_id,project_id" },
  );
}

export async function lastSeenAt(projectId: string, userId: string): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.from("project_note_views")
    .select("seen_at").eq("user_id", userId).eq("project_id", projectId).maybeSingle();
  return data?.seen_at ?? null;
}

// ── Pure helpers ─────────────────────────────────────────────────────────

/**
 * Notes added since this user last looked, by somebody else.
 *
 * Your own note is never unread to you, and a resolved note is not an
 * outstanding thing to read — it is a closed one.
 */
export function unreadCount(
  notes: ProjectNote[], userId: string, seenAt: string | null,
): number {
  const since = seenAt ? Date.parse(seenAt) : 0;
  return notes.filter(n =>
    n.authorId !== userId &&
    Date.parse(n.createdAt) > since,
  ).length;
}

/** Open notes on one anchor, so a row can show a badge. */
export function openNotesFor(notes: ProjectNote[], anchorId: string): ProjectNote[] {
  return notes.filter(n => !n.parentId && n.anchorId === anchorId && !n.resolvedAt);
}

export interface NoteFilter {
  /** "open" is the to-do list; "all" includes what has been dealt with. */
  status?: "open" | "resolved" | "all";
  /** Only notes this user wrote. */
  mine?: boolean;
  /** Only notes naming this user. */
  mentioningMe?: boolean;
  search?: string;
}

/**
 * The one list behind both personas: unresolved is a worklist, mentioning-me
 * is an inbox. Replies are folded into their parent rather than listed.
 */
export function filterNotes(
  notes: ProjectNote[], userId: string, f: NoteFilter = {},
): ProjectNote[] {
  const { status = "open", mine = false, mentioningMe = false, search = "" } = f;
  const q = search.trim().toLowerCase();
  return notes.filter(n => {
    if (n.parentId) return false;
    if (status === "open" && n.resolvedAt) return false;
    if (status === "resolved" && !n.resolvedAt) return false;
    if (mine && n.authorId !== userId) return false;
    if (mentioningMe && !n.mentions.includes(userId)) return false;
    if (q && !n.body.toLowerCase().includes(q)
          && !(n.anchorLabel ?? "").toLowerCase().includes(q)) return false;
    return true;
  });
}

export function repliesTo(notes: ProjectNote[], parentId: string): ProjectNote[] {
  return notes
    .filter(n => n.parentId === parentId)
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

export interface Mentionable { id: string; name: string }

/**
 * Resolve "@name" to user ids.
 *
 * Longest name first, and a match CONSUMES the text it covered: "@Alex Bruce"
 * contains "@Alex", so without blanking it out an org with both would tag two
 * people from one mention. A name must also end at a word boundary, or "@Alex"
 * would fire on "@Alexander".
 *
 * Case-insensitive, because nobody types a colleague's capitalisation.
 */
export function parseMentions(body: string, people: Mentionable[]): string[] {
  let hay = body.toLowerCase();
  const found = new Set<string>();
  const isWordChar = (c: string | undefined) => !!c && /[a-z0-9]/i.test(c);

  [...people]
    .filter(p => p.name)
    .sort((a, b) => b.name.length - a.name.length)
    .forEach(p => {
      const needle = `@${p.name.toLowerCase()}`;
      let from = 0;
      for (;;) {
        const i = hay.indexOf(needle, from);
        if (i === -1) break;
        const after = hay[i + needle.length];
        if (!isWordChar(after)) {
          found.add(p.id);
          hay = hay.slice(0, i) + " ".repeat(needle.length) + hay.slice(i + needle.length);
        }
        from = i + 1;
      }
    });
  return [...found];
}

/**
 * Who can be mentioned: the organisation's members.
 *
 * Two queries rather than a join, because the foreign key between
 * org_members and profiles is not named consistently enough to rely on from
 * the client. Falls back to an empty list, which only costs autocomplete.
 */
export async function loadMentionablePeople(orgId: string | null): Promise<Mentionable[]> {
  if (!supabase || !orgId) return [];
  const { data: members } = await supabase
    .from("org_members").select("user_id").eq("org_id", orgId);
  const ids = (members ?? []).map((m: any) => m.user_id).filter(Boolean);
  if (ids.length === 0) return [];
  const { data: profiles } = await supabase
    .from("profiles").select("id, display_name").in("id", ids);
  return (profiles ?? [])
    .map((p: any) => ({ id: p.id, name: p.display_name || "" }))
    .filter((p: Mentionable) => p.name);
}
